import { lazy, useEffect, Fragment, Suspense, useContext } from 'react';
import get from 'lodash.get';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import * as tracking from 'dibs-tracking';
import { getAbTestTrackingData } from 'dibs-buyer-layout/exports/clientABTestHelpers';
import authModalLoader from 'dibs-buyer-layout/src/utils/AuthModalLoader';

// components
import { SharedFollowSearchProvider } from '../../shared/SharedFollowSearchProvider/SharedFollowSearchProvider';
import { SharedFollowTopButton } from '../../shared/SharedFollowTopButton/SharedFollowTopButton';
import { SbRespFollowSearchBannerLazy } from '../SbRespFollowSearchBanner/SbRespFollowSearchBannerLazy';
import { SbSharedTitle } from '../SbSharedTitle/SbSharedTitle';
import { SbSharedPageBreadcrumbs } from '../SbSharedPageBreadcrumbs/SbSharedPageBreadcrumbs';
import { SbSharedTopTileController } from '../SbSharedTopTileController/SbSharedTopTileController';
import { SbRespCategoryTiles } from '../SbRespCategoryTiles/SbRespCategoryTiles';
import { SbRespDesignTiles } from '../SbRespDesignTiles/SbRespDesignTiles';
import { SbSharedBillboardAd } from '../SbSharedBillboardAd/SbSharedBillboardAd';
import { SbRespNullSearchResults } from '../SbRespNullSearchResults/SbRespNullSearchResults';
import { SbRespInteriorsWordpressArticlesWrapper } from '../SbRespInteriorsModule/SbRespInteriorsWordpressArticlesWrapper';
import { SharedRelatedPages } from '../../shared/SharedRelatedPages/SharedRelatedPages';
import { SbSharedRelatedCreators } from '../SbSharedRelatedCreators/SbSharedRelatedCreators';
import { SharedRespLinksBlock } from '../../shared/SharedRespLinksBlock/SharedRespLinksBlock';
import { SbRespNotificationUnavailableItem } from '../SbRespNotifications/SbRespNotificationUnavailableItem/SbRespNotificationUnavailableItem';
import { SbRespPriceTiles } from '../SbRespPriceTiles/SbRespPriceTiles';
import { TradePromoLoader } from '../../global/TradePromo/TradePromoLoader';
import { SbRespTradeOnApprovalLoadingBanner } from '../SbRespTradeOnApprovalPromo/SbRespTradeOnApprovalPromoBanner/SbRespTradeOnApprovalLoadingBanner';
import { SbSharedPersonalizedFilterToast } from '../SbSharedPersonalizedFilterToast/SbSharedPersonalizedFilterToast';
import { SbRespMainContent } from '../SbRespMainContent/SbRespMainContent';
import { SbSharedForceSearchLink } from '../SbSharedForceSearchLink/SbSharedForceSearchLink';
import { SbRespContentModuleSelector } from '../SbRespContentModuleSelector/SbRespContentModuleSelector';
import { SbSharedControlledHeadingWrapper } from '../SbSharedControlledHeadingWrapper/SbSharedControlledHeadingWrapper';
import { ServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext';
import { hasAppliedCategories } from '../../utils/categoryHelpers';
import { trackRerankEngagementScoreTest } from '../../utils/tracking/searchBrowse/cdnTestTracking';
import { SbSharedRelatedSearches } from '../SbSharedRelatedSearches/SbSharedRelatedSearches';
import { SbRespL1Landing } from '../SbRespL1Landing/SbRespL1Landing';
import { SharedFAQ } from '../../shared/SharedFAQ/SharedFAQ';
import { RecentlyViewedItemsLazy } from 'dibs-recent-history/exports/RecentlyViewedItemsLazy';

// constants
import { pageTypeConstants as pageTypes } from '../../constants/pageTypeConstants';
import { FILTER_REFINE_SEARCH } from '../SbSharedRefineMenu/sbSharedRefineMenuConstants';
import {
    MIN_THIN_PAGE_COUNT,
    REVEAL_RELATED_CREATORS_COUNT_DESKTOP,
} from '../../constants/sbConstants';

const { SEARCH } = pageTypes;

// styles
import styles from './SbRespComponents.scss';

const SbRespTradeOnApprovalPromoBannerLazy = lazy(() =>
    import(
        /* webpackChunkName: "TradeOnApprovalPromo" */ '../SbRespTradeOnApprovalPromo/SbRespTradeOnApprovalPromoBanner/SbRespTradeOnApprovalPromoBannerLazy'
    )
);

function getSearchTerm(filters, isSearchPage) {
    const searchKey = isSearchPage ? SEARCH : FILTER_REFINE_SEARCH;

    return get(
        filters.filter(({ name }) => name === searchKey),
        '[0].values[0].urlLabel'
    );
}

function SbRespComponentsComponent(props) {
    const {
        viewer,
        currency,
        itemSearch,
        user,
        generatedUriRef,
        isClient,
        relay,
        relayVariables,
        shippingFilters,
    } = props;

    const { isSbTopNavVariant } = useContext(ServerVarsContext);

    const {
        totalResults,
        appliedFilters,
        pageType,
        internalBuyLinks = [],
        canBeLocationSearch,
        displayUriRef,
        isL1LandingPage,
    } = itemSearch;

    const { isRelatedCreatorsModuleEnabled } = viewer;

    useEffect(() => {
        if (canBeLocationSearch) {
            const trackingData = getAbTestTrackingData(document.cookie, 'SEARCH_LOCATION_TEST');
            tracking.trackAbTestEvent(trackingData, 'SEARCH_BROWSE_DESKTOP');
        }
    }, [canBeLocationSearch]);

    useEffect(() => {
        const trackingData = getAbTestTrackingData(document.cookie, 'NEW_TAB_TEST');
        tracking.trackAbTestEvent(trackingData, 'results_pageview');
    }, []);

    const isSearch = pageType === SEARCH;
    const showNullSearchResultPage = !totalResults;
    const isThinPage = totalResults < MIN_THIN_PAGE_COUNT;
    const isThinBrowsePage = !isSearch && isThinPage;
    const searchTerm = getSearchTerm(appliedFilters, isSearch);
    const hasCategories = hasAppliedCategories(appliedFilters);

    useEffect(() => {
        trackRerankEngagementScoreTest({
            isSearch,
            hasCategories,
            pageContext: 'SEARCH_RESPONSIVE',
        });
        // displayUriRef is needed to refire tracking after applied filters were updated.
    }, [isSearch, hasCategories, displayUriRef]);

    if (showNullSearchResultPage) {
        return (
            <SbRespNullSearchResults
                user={user}
                viewer={viewer}
                searchTerm={searchTerm}
                uriRef={generatedUriRef}
                itemSearch={itemSearch}
                isClient={isClient}
            />
        );
    }

    return (
        <SharedFollowSearchProvider
            user={user}
            itemSearch={itemSearch}
            uriRef={generatedUriRef}
            searchTerm={searchTerm}
            thinTrackingEnabled={isThinPage}
        >
            {({
                showFollowButton,
                isFollowing,
                followSearchValue,
                refetchFollow,
                isCategorySearch,
            }) => (
                <Fragment>
                    {/* Breadcrumbs */}
                    {!isSearch && <SbSharedPageBreadcrumbs itemSearch={itemSearch} />}
                    <SbSharedControlledHeadingWrapper itemSearch={itemSearch}>
                        {Heading => (
                            <>
                                {isL1LandingPage ? (
                                    /* L1 Page Header */
                                    <SbRespL1Landing itemSearch={itemSearch} viewer={viewer} />
                                ) : (
                                    /* Page Header */
                                    <div className={styles.headerLeftWrapper}>
                                        <div className={styles.headerContainer}>
                                            <div className={styles.headerLeft}>
                                                <SbSharedTitle
                                                    itemSearch={itemSearch}
                                                    searchTerm={searchTerm}
                                                    Heading={Heading}
                                                    showFollowButton={showFollowButton}
                                                    headerWrapperClass={
                                                        isSbTopNavVariant
                                                            ? styles.titleHeaderWrapper
                                                            : undefined
                                                    }
                                                >
                                                    <SharedFollowTopButton
                                                        user={user}
                                                        showFollowButton={showFollowButton}
                                                        followSearchValue={followSearchValue}
                                                        isFollowing={isFollowing}
                                                        refetchFollow={refetchFollow}
                                                        isCategorySearch={isCategorySearch}
                                                        itemSearch={itemSearch}
                                                    />
                                                </SbSharedTitle>
                                            </div>
                                        </div>
                                        {relayVariables.isTrade && isClient && (
                                            <div className={styles.tradePromoMessage}>
                                                <TradePromoLoader
                                                    showIcon
                                                    user={user}
                                                    displayInfo={null}
                                                    item={null}
                                                />
                                            </div>
                                        )}
                                        {/* Related Searches */}
                                        <SbSharedRelatedSearches itemSearch={itemSearch} />
                                    </div>
                                )}

                                {/* Content Module */}
                                <SbRespContentModuleSelector itemSearch={itemSearch} />

                                <SbSharedForceSearchLink
                                    itemSearch={itemSearch}
                                    searchTerm={searchTerm}
                                />

                                {/* Broad Search Category Tiles, or, for trade users, Price Range Tiles or On-Approval banner)*/}
                                <SbSharedTopTileController
                                    itemSearch={itemSearch}
                                    categoryTiles={<SbRespCategoryTiles itemSearch={itemSearch} />}
                                    designTiles={<SbRespDesignTiles itemSearch={itemSearch} />}
                                    tradeOnApprovalBanner={
                                        /* FINDING-13641 Workaround until React Suspense supports server-side
                                         * rendering > https://reactjs.org/docs/code-splitting.html#reactlazy
                                         */
                                        isClient ? (
                                            <Suspense
                                                fallback={<SbRespTradeOnApprovalLoadingBanner />}
                                            >
                                                <SbRespTradeOnApprovalPromoBannerLazy
                                                    itemSearch={itemSearch}
                                                    viewer={viewer}
                                                />
                                            </Suspense>
                                        ) : (
                                            <SbRespTradeOnApprovalLoadingBanner />
                                        )
                                    }
                                    priceTiles={
                                        <SbRespPriceTiles itemSearch={itemSearch} viewer={viewer} />
                                    }
                                    viewer={viewer}
                                />
                                <SbRespMainContent
                                    currency={currency}
                                    itemSearch={itemSearch}
                                    viewer={viewer}
                                    user={user}
                                    isClient={isClient}
                                    isThinPage={isThinPage}
                                    isThinBrowsePage={isThinBrowsePage}
                                    relay={relay}
                                />

                                {/* Related Searches With Title */}
                                <SbSharedRelatedSearches itemSearch={itemSearch} showTitle />

                                {/* Follow Search */}
                                {isClient && (
                                    <SbRespFollowSearchBannerLazy
                                        viewer={viewer}
                                        user={user}
                                        showFollowButton={showFollowButton}
                                        isFollowing={isFollowing}
                                        followSearchValue={followSearchValue}
                                        uriRef={generatedUriRef}
                                        buttonSize="large"
                                        refetchFollow={refetchFollow}
                                        isCategorySearch={isCategorySearch}
                                    />
                                )}
                                {/* Ad */}
                                {!isThinPage && (
                                    <SbSharedBillboardAd
                                        itemSearch={itemSearch}
                                        adWidth="desktop"
                                    />
                                )}
                                {/* Interiors and/or Editorial */}
                                <SbRespInteriorsWordpressArticlesWrapper
                                    itemSearch={itemSearch}
                                    viewer={viewer}
                                    user={user}
                                    isClient={isClient}
                                />
                                {/* Related Creators for creator pages */}
                                {isRelatedCreatorsModuleEnabled && (
                                    <SbSharedRelatedCreators
                                        itemSearch={itemSearch}
                                        revealCreatorCount={REVEAL_RELATED_CREATORS_COUNT_DESKTOP}
                                    />
                                )}
                                {/* Questions & Answers */}
                                <SharedFAQ itemSearch={itemSearch} />
                                {/* Recently Viewed Items */}
                                <RecentlyViewedItemsLazy
                                    centerTitle
                                    wrapperClassName={styles.recentlyViewed}
                                    authModalLoader={authModalLoader}
                                />
                                {/*  Related Pages */}
                                <SharedRelatedPages
                                    viewer={viewer}
                                    itemSearch={itemSearch}
                                    LinksBlockComponent={SharedRespLinksBlock}
                                />
                                {/*  More Ways to Browse */}
                                <SharedRespLinksBlock
                                    viewer={viewer}
                                    item={null}
                                    itemSearch={itemSearch}
                                    links={internalBuyLinks}
                                />
                            </>
                        )}
                    </SbSharedControlledHeadingWrapper>

                    {/* Unavailable Item Modal */}
                    <SbRespNotificationUnavailableItem />
                    {/* Personalized Filter Toast */}
                    <SbSharedPersonalizedFilterToast shippingFilters={shippingFilters} />
                </Fragment>
            )}
        </SharedFollowSearchProvider>
    );
}

SbRespComponentsComponent.propTypes = {
    itemSearch: PropTypes.object.isRequired,
    user: PropTypes.object,
    isClient: PropTypes.bool,
    viewer: PropTypes.object,
    generatedUriRef: PropTypes.string,
    relay: PropTypes.object.isRequired,
    currency: PropTypes.string.isRequired,
    internalBuyLinks: PropTypes.object,
    relayVariables: PropTypes.shape({
        isTrade: PropTypes.bool,
    }).isRequired,
    shippingFilters: PropTypes.array,
};

const mapStateToProps = state => {
    const { filters, relayVariables } = state;
    const { generatedUriRef, refetchInFlight } = filters;

    return {
        refetchInFlight,
        generatedUriRef,
        relayVariables: relayVariables.variables,
    };
};

export const SbRespComponents = createFragmentContainer(
    connect(mapStateToProps)(SbRespComponentsComponent),
    {
        itemSearch: graphql`
            fragment SbRespComponents_itemSearch on ItemSearchQueryConnection {
                totalResults
                pageType
                canBeLocationSearch
                isL1LandingPage
                ...SbSharedControlledHeadingWrapper_itemSearch
                ...SbSharedForceSearchLink_itemSearch
                ...SbSharedSearchSuggestions_itemSearch
                ...SbRespNullSearchResults_itemSearch
                ...SbSharedPageBreadcrumbs_itemSearch
                ...SbSharedTitle_itemSearch
                ...SbSharedTopTileController_itemSearch
                ...SbSharedRelatedSearches_itemSearch
                ...SbRespCategoryTiles_itemSearch
                ...SbRespDesignTiles_itemSearch
                ...SharedFollowSearchProvider_itemSearch
                ...SbSharedBillboardAd_itemSearch
                ...SbRespPriceTiles_itemSearch @include(if: $isTrade)
                ...SbRespTradeOnApprovalPromoBanner_itemSearch @include(if: $isTrade)
                ...SbRespMainContent_itemSearch
                ...SbRespContentModuleSelector_itemSearch
                ...SbRespInteriorsWordpressArticlesWrapper_itemSearch
                ...SharedWordpressArticles_itemSearch
                ...SharedFollowTopButton_itemSearch
                ...SbRespL1Landing_itemSearch
                appliedFilters {
                    name
                    values {
                        urlLabel
                    }
                }
                ...SharedRelatedPages_itemSearch
                ...SbSharedRelatedCreators_itemSearch
                ...SharedRespLinksBlock_itemSearch
                internalBuyLinks {
                    ...SharedRespLinksBlock_links
                }
                displayUriRef
                ...SharedFAQ_itemSearch
            }
        `,
        user: graphql`
            fragment SbRespComponents_user on User {
                ...TradePromo_user @include(if: $isTrade)
                ...SbRespNullSearchResults_user
                ...SbRespFollowSearchBanner_user
                ...SharedFollowTopButton_user
                ...SharedFollowSearchProvider_user
                ...SbRespMainContent_user
                ...SbRespInteriorsWordpressArticlesWrapper_user
            }
        `,
        viewer: graphql`
            fragment SbRespComponents_viewer on Viewer {
                isRelatedCreatorsModuleEnabled: featureFlag(feature: "relatedCreatorsModule")
                ...SharedRelatedPages_viewer
                ...SharedRespLinksBlock_viewer
                ...SbRespNullSearchResults_viewer
                ...SbRespPriceTiles_viewer @include(if: $isTrade)
                ...SbRespMainContent_viewer
                ...SbRespTradeOnApprovalPromoBanner_viewer @include(if: $isTrade)
                ...SbSharedTopTileController_viewer
                ...SbRespInteriorsWordpressArticlesWrapper_viewer
                ...SbRespL1Landing_viewer
            }
        `,
        shippingFilters: graphql`
            fragment SbRespComponents_shippingFilters on ShippingFilterType @relay(plural: true) {
                ...SbSharedPersonalizedFilterToast_shippingFilters
            }
        `,
    }
);
